import PropTypes from 'prop-types';
import React from 'react';
import _get from 'lodash/get';
import _isFunction from 'lodash/isFunction';
import styled from 'styled-components';

import { genID } from 'ravenjs/utils/generate';
import { getThemeProps } from 'ravenjs/utils/theme';
import Divider from 'ravenjs/lib/Divider';

import Item from './Item';
import Key from './Key';
import Value from './Value';

const KeyValuePairsStyled = styled.ul`
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    list-style: none;
    margin: 0;
    padding: 0;
    /**
     * Add all of the remaining styles from theme
     */
    ${getThemeProps('KeyValuePairsNew.styles')};
`;

const KeyComponentStyled = styled.div`
    margin-bottom: 0;

    ${({ theme }) => theme.media.down('md')`
        flex: 1 1 50%;
    `};
    ${({ theme }) => theme.media.up('md')`
        flex: 1 1 85%;
    `};

    ${getThemeProps('KeyValuePairsValue.styles')};
`;

const ValueComponentStyled = styled(Value)`
    margin-bottom: 0;

    ${({ theme }) => theme.media.down('md')`
        font-size: 0.75rem;
        padding: 0.25rem 0;
        flex: 1 1 50%;
        word-break: break-word;
    `};
    ${({ theme }) => theme.media.up('md')`
        flex: 1 1 85%;
        font-size: 1rem;
    `};
`;

const KeyValuePairsNew = ({ items, itemKey, onItemClick, itemValue, keySuffix, showDivider }) => (
    <KeyValuePairsStyled>
        {items.map(item => {
            const theKey = _get(item, itemKey, itemKey);
            const theValue = _get(item, itemValue, itemValue);
            const onClick = _isFunction(onItemClick) ? () => onItemClick(item) : null;
            const { [itemValue]: ValueComponent } = item;

            return (
                <React.Fragment key={genID()}>
                    <Item key={genID('KeyItem')} onClick={onClick}>
                        <Key>{`${theKey}${keySuffix}`}</Key>
                        {React.isValidElement(ValueComponent) ? (
                            <KeyComponentStyled>{ValueComponent}</KeyComponentStyled>
                        ) : (
                            <ValueComponentStyled>{theValue}</ValueComponentStyled>
                        )}
                    </Item>
                    {showDivider && <Divider />}
                </React.Fragment>
            );
        })}
    </KeyValuePairsStyled>
);

KeyValuePairsNew.propTypes = {
    /**
     * The `key` to extract from the item.
     * Key is inserted into the first column of the KeyValuePairsNew.
     */
    itemKey: PropTypes.string,
    /**
     * The `value` to extract from the item.
     * Value is inserted into the second column of the KeyValuePairsNew.
     *
     * @type {[type]}
     */
    itemValue: PropTypes.string,
    /**
     * The list of items for the KeyValuePairsNew
     */
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
    keySuffix: PropTypes.string,
    /**
     * The `onClick` prop for the item.
     */
    onItemClick: PropTypes.func,

    showDivider: PropTypes.bool,
};

KeyValuePairsNew.defaultProps = {
    itemKey: '',
    itemValue: '',
    keySuffix: '',
    onItemClick: null,
    showDivider: false,
};

export default KeyValuePairsNew;
